import {
  IInitialViewpoint,
  IViewpointBimFile,
  IViewpointComponent,
  ITopicPoint,
} from "Data/interfaces/Activities/IViewpointData";

type ModelDictionary = Map<string, Autodesk.Viewing.Model>;

export class ViewpointRestoreService {
  private static normalizeUrn(raw?: string | null): string | null {
    if (!raw || raw.trim().length === 0) {
      return null;
    }
    return raw.startsWith("urn:") ? raw : `urn:${raw}`;
  }

  private static stripUrnPrefix(raw: string): string {
    return raw.replace(/^urn:/i, "");
  }

  private static collectRequiredModelUrns(viewpoint: IInitialViewpoint): Set<string> {
    const urns = new Set<string>();

    viewpoint.BimFiles?.forEach((file) => {
      const normalized =
        ViewpointRestoreService.normalizeUrn(file.Reference) ??
        ViewpointRestoreService.normalizeUrn(file.Filename);

      if (normalized) {
        urns.add(normalized);
      }
    });

    return urns;
  }

  private static waitForRequiredModels(
    viewer: Autodesk.Viewing.GuiViewer3D,
    viewpoint: IInitialViewpoint,
    onReady: () => void,
  ): void {
    const requiredUrns = ViewpointRestoreService.collectRequiredModelUrns(viewpoint);
    const GEOMETRY_LOADED_EVENT =
      (Autodesk as any)?.Viewing?.GEOMETRY_LOADED_EVENT ?? Autodesk?.Viewing.GEOMETRY_LOADED_EVENT;
    const MODEL_COMPLETED_EVENT =
      (Autodesk as any)?.Viewing?.MODEL_COMPLETED_EVENT ??
      (Autodesk as any)?.Viewing?.OBJECT_TREE_CREATED_EVENT ??
      Autodesk?.Viewing.OBJECT_TREE_CREATED_EVENT;

    const hasAllRequiredModels = () => {
      const allModels =
        (viewer.getAllModels && viewer.getAllModels()) ||
        (viewer.model ? [viewer.model] : []);

      if (!allModels || allModels.length === 0) {
        return false;
      }

      const normalizedLoaded = new Set<string>();
      const strippedLoaded = new Set<string>();
      const allModelsLoaded = allModels.every((model: Autodesk.Viewing.Model) => {
        const loaderDone =
          typeof (model as any).isLoadDone === "function"
            ? (model as any).isLoadDone()
            : true;
        return loaderDone;
      });

      allModels.forEach((model) => {
        const data = model?.getData?.();
        const modelUrn = data?.urn ?? (model as any)?.loader?.svfUrn;
        const normalized = ViewpointRestoreService.normalizeUrn(modelUrn);
        if (normalized) {
          normalizedLoaded.add(normalized);
          strippedLoaded.add(ViewpointRestoreService.stripUrnPrefix(normalized));
        }
      });

      if (requiredUrns.size === 0) {
        return normalizedLoaded.size > 0 && allModelsLoaded;
      }

      return Array.from(requiredUrns).every((urn) => {
        const normalized = ViewpointRestoreService.normalizeUrn(urn);
        if (!normalized) {
          return true;
        }
        const stripped = ViewpointRestoreService.stripUrnPrefix(normalized);
        return normalizedLoaded.has(normalized) || strippedLoaded.has(stripped);
      });
    };

    if (hasAllRequiredModels()) {
      onReady();
      return;
    }

    let resolved = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      if (GEOMETRY_LOADED_EVENT !== undefined) {
        viewer.removeEventListener(GEOMETRY_LOADED_EVENT, handleModelEvent);
      }
      if (MODEL_COMPLETED_EVENT !== undefined) {
        viewer.removeEventListener(MODEL_COMPLETED_EVENT, handleModelEvent);
      }
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };

    const handleModelEvent = () => {
      if (resolved || !hasAllRequiredModels()) {
        return;
      }

      resolved = true;
      cleanup();
      onReady();
    };

    if (GEOMETRY_LOADED_EVENT !== undefined) {
      viewer.addEventListener(GEOMETRY_LOADED_EVENT, handleModelEvent);
    }
    if (MODEL_COMPLETED_EVENT !== undefined) {
      viewer.addEventListener(MODEL_COMPLETED_EVENT, handleModelEvent);
    }

    timeoutId = setTimeout(() => {
      if (resolved) {
        return;
      }
      resolved = true;
      cleanup();
      onReady();
    }, 10000);
  }

  static applyViewpoint(viewer: Autodesk.Viewing.GuiViewer3D, viewpoint: IInitialViewpoint, topicPoints?: ITopicPoint[]): void {
    if (!viewer || !viewpoint) {
      console.warn("Viewer or viewpoint is null/undefined");
      return;
    }

    const runRestore = () => {
      const THREE = (window as any).THREE || (Autodesk as any).THREE;
      if (!THREE) {
        console.warn("THREE namespace not available. Skipping viewpoint restore.");
        return;
      }

      const modelMap = this.buildModelDictionary(viewer);
      this.restoreCamera(viewer, viewpoint, topicPoints, false); // animated fly-to

      // Re-apply camera whenever additional models finish loading.
      // Forge auto-fits the view on each GEOMETRY_LOADED_EVENT, which overwrites the
      // viewpoint camera when multiple files load in parallel (only 1 may be done at
      // the time applyViewpoint first runs). We listen for up to MAX_REAPPLY events
      // or 15 seconds, whichever comes first.
      const GEOM_EVENT =
        (Autodesk as any).Viewing?.GEOMETRY_LOADED_EVENT ??
        Autodesk.Viewing.GEOMETRY_LOADED_EVENT;
      let reapplyCount = 0;
      const MAX_REAPPLY = 10;

      const removeReapplyListener = () => {
        viewer.removeEventListener(GEOM_EVENT, onSubsequentModelLoad);
        clearTimeout(reapplyCleanupTimer);
      };

      const onSubsequentModelLoad = () => {
        reapplyCount++;
        if (reapplyCount > MAX_REAPPLY) {
          removeReapplyListener();
          return;
        }
        // Delay the re-apply to let async operations triggered by GEOMETRY_LOADED_EVENT
        // (e.g. AEC.LevelsExtension loading inside getLoadedChanged) settle before we
        // restore the camera. Without this delay, our listener fires during the event
        // but the extension finishes loading afterward and resets the camera again.
        setTimeout(() => {
          ViewpointRestoreService.restoreCamera(viewer, viewpoint, topicPoints);
        }, 300);
      };

      viewer.addEventListener(GEOM_EVENT, onSubsequentModelLoad);
      const reapplyCleanupTimer = setTimeout(removeReapplyListener, 15000);

      this.restoreVisibility(viewer, viewpoint.Visibility, modelMap);

      // When DefaultVisibility=false (hideAll mode), BCF-imported selection components
      // may not be listed in the visibility exceptions and would remain hidden.
      // Force-show them using the same IfcGuid resolution path.
      if (viewpoint.Visibility && !viewpoint.Visibility.DefaultVisibility) {
        const ifcOnlySelected = (viewpoint.Selection ?? []).filter(
          c => !c.ComponentDetails?.ModelUrn && !!c.IfcGuid,
        );
        if (ifcOnlySelected.length > 0) {
          const allModels = viewer.getAllModels ? viewer.getAllModels() : [viewer.model].filter(Boolean);
          this.resolveAndApplyIfcVisibility(viewer, ifcOnlySelected, allModels, false /* force show */);
        }
      }

      this.restoreSelection(viewer, viewpoint.Selection, modelMap);
      this.restoreBimFiles(viewer, viewpoint.BimFiles, modelMap);

      viewer.impl.sceneUpdated(true);
      viewer.impl.invalidate(true, true, true);

      // Wait for camera to settle before applying clipping planes
      // Camera adjustments can remove planes if applied too early
      let cameraSettleTimeout: ReturnType<typeof setTimeout> | null = null;
      const CAMERA_SETTLE_DELAY = 200;
      
      const applyClippingPlanesAfterCameraSettles = () => {
        this.restoreClippingPlanes(viewer, viewpoint.ClippingPlanes);
        viewer.removeEventListener(Autodesk?.Viewing.CAMERA_CHANGE_EVENT, handleCameraChange);
      };
      
      const handleCameraChange = () => {
        if (cameraSettleTimeout) {
          clearTimeout(cameraSettleTimeout);
        }
        cameraSettleTimeout = setTimeout(applyClippingPlanesAfterCameraSettles, CAMERA_SETTLE_DELAY);
      };
      
      viewer.addEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, handleCameraChange);
      
      setTimeout(() => {
        if (cameraSettleTimeout) {
          clearTimeout(cameraSettleTimeout);
        }
        viewer.removeEventListener(Autodesk?.Viewing.CAMERA_CHANGE_EVENT, handleCameraChange);
        this.restoreClippingPlanes(viewer, viewpoint.ClippingPlanes);
      }, 3000);
    };

    ViewpointRestoreService.waitForRequiredModels(viewer, viewpoint, runRestore);
  }

  private static buildModelDictionary(viewer: Autodesk.Viewing.GuiViewer3D): ModelDictionary {
    const models = viewer.getAllModels ? viewer.getAllModels() : [viewer.model];
    const dictionary: ModelDictionary = new Map();

    if (!models) {
      return dictionary;
    }

    models.forEach((model) => {
      const urn = model?.getData()?.urn;
      if (!urn) {
        return;
      }

      dictionary.set(urn, model);
      if (!urn.startsWith("urn:")) {
        dictionary.set(`urn:${urn}`, model);
      } else {
        dictionary.set(urn.replace(/^urn:/, ""), model);
      }
    });

    return dictionary;
  }

  /**
   * Restores the camera viewpoint using the viewer's navigation API.
   */
  private static restoreCamera(viewer: Autodesk.Viewing.GuiViewer3D, viewpoint: IInitialViewpoint, topicPoints?: ITopicPoint[], immediate = true): void {
    const cameraDto = viewpoint.Camera;
    
    if (!cameraDto) {
      console.warn("No camera data provided");
      return;
    }

    if (typeof viewer.restoreState !== "function") {
      console.error("viewer.restoreState is not available. Cannot restore viewpoint.");
      return;
    }

    const THREE = (window as any).THREE || (Autodesk as any).THREE;
    if (!THREE) {
      console.warn("THREE not available");
      return;
    }

    const allModels = viewer.getAllModels ? viewer.getAllModels() : [viewer.model].filter(Boolean);

    // Get the full model-to-viewer transform from the first loaded model.
    // This matrix converts IFC/BCF world coordinates into Forge viewer space and
    // incorporates everything Forge applied during loading: the globalOffset
    // translation (to keep large-coordinate models near the render origin), any
    // placement transform, and any axis/unit-scale conversion. Using bare
    // `- globalOffset` misses rotation and scale, causing direction and up vectors
    // to point into the wrong viewer-space axes when those transforms are present.
    let modelToViewer: any = null;
    if (allModels && allModels.length > 0) {
      const model = allModels[0];
      modelToViewer = typeof (model as any).getModelToViewerTransform === 'function'
        ? (model as any).getModelToViewerTransform()
        : null;

      if (!modelToViewer) {
        // Fallback: build a pure-translation matrix from the raw globalOffset.
        const data = model?.getData();
        const go = data?.globalOffset ?? { x: 0, y: 0, z: 0 };
        modelToViewer = new THREE.Matrix4().makeTranslation(-(go.x || 0), -(go.y || 0), -(go.z || 0));
      }
    } else {
      modelToViewer = new THREE.Matrix4(); // identity — no models yet
    }

    // BCF-imported viewpoints (BimFiles === null) store coordinates in metres per
    // the BCF spec / most authoring tools (BIM Collab, Revit BCF export, etc.),
    // regardless of the IFC model's internal unit. Coordly-captured viewpoints
    // (BimFiles present) store in model-native units (viewer space + globalOffset),
    // so no conversion is needed for those.
    // model.getUnitScale() returns metres-per-model-unit (e.g. 0.001 for mm models).
    // Dividing BCF metres by that value gives model-native units.
    const isBcfImport = !viewpoint.BimFiles || viewpoint.BimFiles.length === 0;
    let bcfToModelScale = 1.0;
    if (isBcfImport && allModels && allModels.length > 0) {
      const metersPerUnit: number | null = typeof (allModels[0] as any).getUnitScale === 'function'
        ? (allModels[0] as any).getUnitScale()
        : null;
      if (metersPerUnit && metersPerUnit > 0 && metersPerUnit !== 1) {
        bcfToModelScale = 1 / metersPerUnit;
      }
    }
    // Convert BCF/IFC world-space camera vectors into Forge viewer space.
    // • position  : scale to model units, then full 4×4 transform
    // • direction/up: rotation only via transformDirection (no translation, normalises)
    const position = new THREE.Vector3(
      cameraDto.ViewPoint.X * bcfToModelScale,
      cameraDto.ViewPoint.Y * bcfToModelScale,
      cameraDto.ViewPoint.Z * bcfToModelScale,
    ).applyMatrix4(modelToViewer);

    const viewerDirectionVec = new THREE.Vector3(cameraDto.Direction.X, cameraDto.Direction.Y, cameraDto.Direction.Z)
      .transformDirection(modelToViewer);

    const viewerUpVec = new THREE.Vector3(cameraDto.UpVector.X, cameraDto.UpVector.Y, cameraDto.UpVector.Z)
      .transformDirection(modelToViewer);

    // Calculate a reasonable distance for the target using model bounding box
    let targetDistance: number;

    if (allModels && allModels.length > 0) {
      const bbox = allModels[0].getBoundingBox();
      if (bbox) {
        const size = bbox.getSize(new THREE.Vector3());
        targetDistance = (size.length() / 2) * 0.41;
      } else {
        targetDistance = 100;
      }
    } else {
      targetDistance = 100;
    }
    // Calculate target position from camera position + direction * distance
    const targetPosition = position.clone().add(viewerDirectionVec.clone().multiplyScalar(targetDistance));
    
    const isOrthographic = cameraDto.Type === "Orthogonal";
    const aspectRatio = cameraDto.AspectRatio && cameraDto.AspectRatio > 0 ? cameraDto.AspectRatio : 1.777;
    const fov = isOrthographic ? 45 : (cameraDto.FieldOfView || 45);

    // Use restoreState for atomic, animation-free camera restoration.
    // Mixing direct camera object mutations with navigation API calls (setView,
    // setCameraUpVector, setVerticalFov) can cause conflicts because each navigation
    // call may trigger internal state reconciliation or smooth-transition animations
    // that override the previous call. restoreState applies all properties at once
    // and the immediate=true flag skips any transition animation.
    const viewportState: Record<string, unknown> = {
      name: '',
      eye: [position.x, position.y, position.z],
      target: [targetPosition.x, targetPosition.y, targetPosition.z],
      up: [viewerUpVec.x, viewerUpVec.y, viewerUpVec.z],
      worldUpVector: [0, 0, 1],
      pivotPoint: [targetPosition.x, targetPosition.y, targetPosition.z],
      distanceToOrbit: targetDistance,
      aspectRatio,
      fov,
      isOrtho: isOrthographic,
    };

    if (isOrthographic && cameraDto.ViewToWorldScale) {
      viewportState.orthoScale = cameraDto.ViewToWorldScale;
    }

    (viewer as any).restoreState({ viewport: viewportState }, null, immediate);

    // restoreState does not reliably apply FOV — explicitly set it after.
    // Use false to preserve camera position (don't adjust for FOV change).
    if (!isOrthographic) {
      if (immediate) {
        // Instant snap — set FOV right away.
        viewer.navigation.setVerticalFov(fov, false);
      } else {
        // Animated transition — defer FOV until Forge finishes the fly-to so
        // we don't interrupt the animation with a conflicting camera mutation.
        const TRANSITION_DONE =
          (Autodesk as any).Viewing?.CAMERA_TRANSITION_COMPLETED ??
          'cameraTransitionCompleted';
        const onTransitionDone = () => {
          viewer.removeEventListener(TRANSITION_DONE, onTransitionDone);
          viewer.navigation.setVerticalFov(fov, false);
        };
        viewer.addEventListener(TRANSITION_DONE, onTransitionDone);
        // Safety fallback — if the event never fires, apply FOV after 3 s.
        setTimeout(() => {
          viewer.removeEventListener(TRANSITION_DONE, onTransitionDone);
          viewer.navigation.setVerticalFov(fov, false);
        }, 3000);
      }
    }
  }

  private static restoreVisibility(
    viewer: Autodesk.Viewing.GuiViewer3D,
    visibility: IInitialViewpoint['Visibility'],
    modelMap: ModelDictionary,
  ): void {
    viewer.showAll();

    if (!visibility) {
      return;
    }

    const defaultVisible = visibility.DefaultVisibility;

    // Split exceptions into those with a resolved ModelUrn (Coordly-captured)
    // and those with only an IfcGuid (BCF-imported, no ComponentDetail in DB).
    const allExceptions = visibility.Exceptions ?? [];
    const dbIdExceptions = allExceptions.filter(c => !!c.ComponentDetails?.ModelUrn);
    // For BCF-imported: also check ComponentDetails.DbId if it's an IFC GUID string (not integer dbId)
    const ifcGuidExceptions = allExceptions.filter(c => {
      const hasModelUrn = !!c.ComponentDetails?.ModelUrn;
      const hasIfcGuid = !!c.IfcGuid;
      const hasStringDbId = !!c.ComponentDetails?.DbId && isNaN(Number(c.ComponentDetails.DbId));
      return !hasModelUrn && (hasIfcGuid || hasStringDbId);
    });

    // Apply the base visibility state.
    if (!defaultVisible) {
      viewer.hideAll();
    }

    // Apply resolved (Coordly-style) exceptions immediately.
    if (dbIdExceptions.length > 0) {
      const grouped = this.groupComponentsByModel(dbIdExceptions, modelMap);
      grouped.forEach((dbIds, model) => {
        if (dbIds.length === 0) return;
        if (defaultVisible) {
          viewer.hide(dbIds, model);
        } else {
          viewer.show(dbIds, model);
        }
      });
    }

    // Resolve IfcGuid-based (BCF-imported) exceptions asynchronously.
    if (ifcGuidExceptions.length > 0) {
      const allModels = viewer.getAllModels ? viewer.getAllModels() : [viewer.model].filter(Boolean);
      this.resolveAndApplyIfcVisibility(viewer, ifcGuidExceptions, allModels, defaultVisible);
    }
  }

  /**
   * Converts an IFC compressed GUID (22-char base-64 encoded, e.g. "20B0Uw2htYiG01000007PG")
   * to standard UUID format (e.g. "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx").
   *
   * Forge stores element identifiers in the property database (LcOaNode:LcOaNodeGuid)
   * as UUIDs, not as IFC compressed GUIDs, so this conversion is required before searching.
   */
  private static ifcGuidToUuid(ifcGuid: string): string {
    if (ifcGuid.length !== 22) return '';
    const CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz_$';

    const b64 = (start: number, len: number): number => {
      let n = 0;
      for (let i = 0; i < len; i++) {
        const idx = CHARS.indexOf(ifcGuid[start + i]);
        if (idx < 0) return -1;
        n = n * 64 + idx;
      }
      return n;
    };

    // Decode into 6 numbers: 12-bit + 5×24-bit (IFC GUID encoding groups)
    const n = [b64(0, 2), b64(2, 4), b64(6, 4), b64(10, 4), b64(14, 4), b64(18, 4)];
    if (n.some(v => v < 0)) return '';

    // Repack into 4×32-bit unsigned integers (the 128-bit UUID value).
    // All arithmetic uses multiplication/division (not << / >>) to avoid
    // JavaScript's signed-32-bit truncation for values that exceed 2^31.
    const p = [
      n[0] * 1048576 + Math.floor(n[1] / 16),
      (n[1] % 16) * 268435456 + n[2] * 16 + Math.floor(n[3] / 1048576),
      (n[3] % 1048576) * 4096 + Math.floor(n[4] / 4096),
      (n[4] % 4096) * 1048576 + n[5],
    ];

    const h = (num: number, len: number): string =>
      Math.floor(num).toString(16).padStart(len, '0');

    return (
      `${h(p[0], 8)}-` +
      `${h(Math.floor(p[1] / 65536), 4)}-` +
      `${h(p[1] % 65536, 4)}-` +
      `${h(Math.floor(p[2] / 65536), 4)}-` +
      `${h(p[2] % 65536, 4)}${h(p[3], 8)}`
    );
  }

  /**
   * For BCF-imported viewpoints, visibility exceptions carry only an IfcGuid
   * (no ModelUrn / integer dbId). Converts each IfcGuid to UUID format and
   * resolves dbIds via model.getBulkProperties() on the LcOaNodeGuid property,
   * then applies show/hide across all loaded models.
   *
   * model.search() is NOT used because Forge marks internal node properties
   * (LcOaNodeGuid) as non-searchable, so search always returns 0 results.
   */
  private static resolveAndApplyIfcVisibility(
    viewer: Autodesk.Viewing.GuiViewer3D,
    exceptions: IViewpointComponent[],
    models: Autodesk.Viewing.Model[],
    defaultVisible: boolean,
  ): void {
    if (!exceptions.length || !models.length) return;

    // Extract IFC GUIDs from both IfcGuid field and ComponentDetails.DbId (when it's a string, not integer)
    const ifcGuids = exceptions.map(c => {
      const fromIfcGuid = c.IfcGuid;
      const fromDbId = c.ComponentDetails?.DbId && isNaN(Number(c.ComponentDetails.DbId)) 
        ? c.ComponentDetails.DbId 
        : null;
      return fromIfcGuid || fromDbId;
    }).filter(Boolean) as string[];
    if (!ifcGuids.length) return;

    // Convert IFC compressed GUIDs to UUID format (how Forge stores them in LcOaNodeGuid).
    const uuidSet: Record<string, boolean> = {};
    ifcGuids.forEach(g => {
      const uuid = ViewpointRestoreService.ifcGuidToUuid(g);
      if (uuid) uuidSet[uuid] = true;
    });
    // Also keep the raw 22-char IFC GUIDs — some Forge translations store GlobalId in that format.
    const rawIfcGuidSet: Record<string, boolean> = {};
    ifcGuids.forEach(g => { rawIfcGuidSet[g] = true; });

    const uuidCount = Object.keys(uuidSet).length;
    if (!uuidCount) return;

    models.forEach((model, idx) => {
      const modelUrn = (model as any).getData?.()?.urn ?? `model[${idx}]`;

      model.getObjectTree((tree: any) => {
        const allDbIds: number[] = [];
        tree.enumNodeChildren(tree.getRootId(), (dbId: number) => { allDbIds.push(dbId); }, true);

        (model as any).getBulkProperties(
          allDbIds,
          { includeInternals: true },
          (results: any[]) => {
            // Match against UUID format AND raw 22-char IFC GUID format.
            const matchedDbIds: number[] = [];
            results.forEach(result => {
              (result.properties || []).forEach((p: any) => {
                const val = String(p.displayValue);
                if (uuidSet[val] || rawIfcGuidSet[val]) {
                  matchedDbIds.push(result.dbId);
                }
              });
            });
            if (!matchedDbIds.length) {
              return;
            }
            if (defaultVisible) {
              viewer.hide(matchedDbIds, model);
            } else {
              viewer.show(matchedDbIds, model);
            }
          },
          (err: any) => {
            console.warn(`[ViewpointRestore] getBulkProperties failed on ${modelUrn}: ${err}`);
          },
        );
      }, () => {
        console.warn(`[ViewpointRestore] getObjectTree failed on ${modelUrn}`);
      });
    });
  }

  private static restoreSelection(
    viewer: Autodesk.Viewing.GuiViewer3D,
    selection: IInitialViewpoint["Selection"],
    modelMap: ModelDictionary
  ): void {
    try {
      viewer.clearSelection();
      if (typeof viewer.setAggregateSelection === 'function') {
        viewer.setAggregateSelection([]);
      }
    } catch (e) {
      console.warn("Error clearing selection:", e);
    }

    if (!selection || selection.length === 0) {
      return;
    }

    const grouped = this.groupComponentsByModel(selection, modelMap);
    const aggregate: any[] = [];

    grouped.forEach((dbIds, model) => {
      if (!Array.isArray(dbIds) || dbIds.length === 0) {
        return;
      }
      const filteredIds = dbIds.filter((id) => Number.isFinite(id));
      if (filteredIds.length === 0) {
        return;
      }
      if (model) {
        aggregate.push({ model, selection: filteredIds });
      }
    });

    if (aggregate.length > 0) {
      const validAggregate = aggregate.filter((entry) => {
        return (
          entry &&
          entry.model &&
          Array.isArray(entry.selection) &&
          entry.selection.length > 0 &&
          entry.selection.every((id: any) => Number.isFinite(id))
        );
      });
      
      if (validAggregate.length > 0) {
        try {
          setTimeout(() => {
            try {
              // Ensure proper format for setAggregateSelection - use 'ids' not 'selection'
              const formattedAggregate = validAggregate.map(({ model, selection }) => ({
                model,
                ids: Array.isArray(selection) ? selection : [selection]
              }));
              
              viewer.setAggregateSelection(formattedAggregate);
            } catch (error) {
              console.error("Error setting aggregate selection:", error);
              
              // Fallback to individual selections
              validAggregate.forEach(({ model, selection: dbIds }) => {
                try {
                  const ids = Array.isArray(dbIds) ? dbIds : [dbIds];
                  viewer.select(ids, model);
                } catch (fallbackError) {
                  console.error("Fallback selection also failed:", fallbackError);
                }
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error in selection timeout:", error);
        }
      }
    }
  }

  private static restoreClippingPlanes(
    viewer: Autodesk.Viewing.GuiViewer3D,
    clippingPlanes: IInitialViewpoint["ClippingPlanes"]
  ): void {
    const THREE = (window as any).THREE || (Autodesk as any).THREE;
    if (!THREE) {
      return;
    }

    if (!clippingPlanes || clippingPlanes.length === 0) {
      (viewer.impl as any).setCutPlanes([]);
      return;
    }

    const planes = clippingPlanes.map((plane) => {
      const normal = new THREE.Vector3(plane.Direction.X, plane.Direction.Y, plane.Direction.Z);
      const location = new THREE.Vector3(plane.Location.X, plane.Location.Y, plane.Location.Z);
      const constant = -normal.dot(location);
      return new THREE.Vector4(normal.x, normal.y, normal.z, constant);
    });

    (viewer.impl as any).setCutPlanes(planes);
  }

  private static restoreBimFiles(
    viewer: Autodesk.Viewing.GuiViewer3D,
    bimFiles: IViewpointBimFile[] | undefined,
    modelMap: ModelDictionary
  ): void {
    if (!bimFiles || bimFiles.length === 0) {
      return;
    }

    bimFiles.forEach((file) => {
      const model =
        modelMap.get(file.Reference) ||
        modelMap.get(file.Reference?.replace(/^urn:/, "") || "");

      if (!model) {
        return;
      }

      const instanceTree = model.getData()?.instanceTree;
      if (!instanceTree) {
        return;
      }

      const rootId = instanceTree.getRootId();
      const visibilityManager = viewer.impl.visibilityManager as any;
      if (visibilityManager?.setNodeOff) {
        visibilityManager.setNodeOff(rootId, !file.IsVisible, model);
      }
    });
  }

  private static groupComponentsByModel(
    components: IViewpointComponent[] | undefined,
    modelMap: ModelDictionary
  ): Map<Autodesk.Viewing.Model, number[]> {
    const grouped = new Map<Autodesk.Viewing.Model, number[]>();

    if (!components) {
      return grouped;
    }

    components.forEach((component) => {
      const modelUrn = component.ComponentDetails?.ModelUrn;
      const dbIdRaw = component.ComponentDetails?.DbId || component.AuthoringToolId;
      const dbId = dbIdRaw ? Number(dbIdRaw) : NaN;

      if (!modelUrn || Number.isNaN(dbId)) {
        return;
      }

      const model =
        modelMap.get(modelUrn) || modelMap.get(modelUrn.replace(/^urn:/, ""));

      if (!model) {
        return;
      }

      const existing = grouped.get(model) || [];
      if (!existing.includes(dbId)) {
        existing.push(dbId);
      }
      grouped.set(model, existing);
    });

    return grouped;
  }
}
